// Workspace settings for the tenant. Tabs: Perfil, Horarios (display), Equipo
// (members + invites), Notificaciones (per-user, /account/notification-
// preferences), Facturación (plan + usage + invoices + CFDI, via GET /billing
// — the Stripe mirror) and Actividad (audit log, CSV export client-side).
//
// Wired: getSettings() -> GET /settings; saveProfile -> PUT /settings/profile.
// The agent's REAL hours live in bot-config horarios (edited in Ajustes del
// agente) — the old PUT /settings/schedule orphan was removed in C2.

import { apiClient } from '../../api/client.js'
import { downloadFromApi } from '../../lib/download.js'

export interface ProfileField {
  label: string
  value: string
  /** Operator-managed field: shown read-only with a lock + who to ask. */
  locked?: boolean
  lockHint?: string
}

export interface ScheduleException {
  date: string
  label: string
  status: string
}

// ── Equipo ──
export type MemberRole = 'Dueño' | 'Administrador' | 'Asesor' | 'Visor'
export type MemberStatus = 'active' | 'pending'
export interface TeamMember {
  /** Discriminates how `id` is acted on: a real member (id = user id) vs a
   *  pending invitation (id = invitation id). */
  kind: 'member' | 'invite'
  id: string
  name: string
  email: string
  initials: string
  role: MemberRole
  /** The canonical role value (tenant_owner/admin/agent/viewer) for editing. */
  roleValue: string
  status: MemberStatus
  /** "En línea", "Hace 1 hora", "Invitada hace 4 días". */
  lastActivity: string
  /** The signed-in user — gets a "Tú" badge and can't be removed. */
  isYou?: boolean
}

// ── Uso / gerente de cuenta (real, from GET /settings) ──
// These used to live nested under a fake `billing` object alongside four
// invented fields (planName/planPrice/cycle/nextInvoice/customerSince) and an
// always-empty `invoices: []`. That object is gone; real plan/price/invoices
// come from GET /billing (below) instead.
export interface UsageMeter {
  id: string
  label: string
  used: number
  /** The tenant's real allowance, or null when the plan is unlimited — in which
   *  case the UI shows a bare count instead of inventing a denominator. */
  total: number | null
  note: string
  tone: 'blue' | 'ok'
}
export interface ManagerInfo {
  name: string
  initials: string
}

// ── Facturación, the real thing (Phase 3a.4b → GET /billing) ──
// Our own mirror of Stripe, never a live Stripe call. Money is in CENTAVOS
// (1_500_000 = $15,000.00 MXN) — format with `formatMxn` below, never raw.

/** Billing truth, distinct from the operational `tenants.status` gate. */
export type SubscriptionStatus =
  | 'comped'
  | 'pending_payment_setup'
  | 'active'
  | 'past_due'
  | 'suspended'
  | 'cancelled'

export interface BillingSubscription {
  status: SubscriptionStatus
  tierKey: string
  /** Centavos. Null only if neither a custom price nor a tier price is set. */
  priceMxn: number | null
  currency: string
  billingCycle: string
  contractStart: string
  contractEndMin: string
  currentPeriodEnd: string | null
  /** False for comped accounts — the Customer Portal has nothing to manage. */
  hasStripeCustomer: boolean
  /** Cancellation already requested; service runs to the period end. */
  cancelAtPeriodEnd: boolean
  /** True once the minimum contract term is over (cancelling is self-serve). */
  contractEndMinPassed: boolean
}

export interface BillingInvoiceRow {
  id: string
  /** Stripe's folio — what the client's accountant will look for. */
  folio: string | null
  /** Present while the invoice is payable: the page where it gets paid. */
  payUrl: string | null
  pdfUrl: string | null
  amountTotalMxn: number
  amountSubtotalMxn: number
  amountTaxMxn: number
  currency: string
  /** Stripe's invoice status: draft | open | paid | void | uncollectible. */
  status: string
  periodStart: string | null
  periodEnd: string | null
  dueDate: string | null
  paidAt: string | null
  createdAt: string
  /**
   * The fiscal invoice (CFDI), which is a different thing from `status`.
   * `status` is about the money; this is about whether the SAT-stamped
   * document the client's accountant needs exists yet.
   */
  cfdi: {
    status: string | null
    uuid: string | null
    issuedAt: string | null
    /** The PAC's own words, present only when the CLINIC can fix it. */
    reason: string | null
    canDownload: boolean
  }
}

export interface BillingPaymentMethod {
  kind: string
  last4: string | null
  brand: string | null
  isDefault: boolean
}

export interface BillingAccount {
  subscription: BillingSubscription | null
  invoices: BillingInvoiceRow[]
  paymentMethods: BillingPaymentMethod[]
}

/** Centavos → "$15,000.00". */
export function formatMxn(centavos: number, currency = 'MXN'): string {
  return (centavos / 100).toLocaleString('es-MX', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  })
}

/** The tenant's own subscription + invoice history + payment methods. */
export async function getBilling(): Promise<BillingAccount> {
  const res = await apiClient.get<{ data: BillingAccount }>('/billing')
  return res.data.data
}

/**
 * Download the stamped CFDI's XML or PDF.
 *
 * Both matter and neither substitutes for the other: the XML is the legally
 * valid document their accountant loads into their system, the PDF is the
 * human-readable representation of it.
 */
export async function downloadCfdi(invoiceId: string, kind: 'xml' | 'pdf'): Promise<void> {
  await downloadFromApi(
    `/billing/invoices/${encodeURIComponent(invoiceId)}/cfdi/${kind}`,
    `CFDI.${kind}`,
  )
}

/** The page where a specific invoice gets paid. */
export async function getInvoicePayUrl(invoiceId: string): Promise<string> {
  const res = await apiClient.post<{ data: { url: string } }>(
    `/billing/invoices/${encodeURIComponent(invoiceId)}/pay-link`,
    {},
  )
  return res.data.data.url
}

/** Ask to cancel. Service always runs to the end of the paid period. */
export async function requestCancellation(): Promise<{ status: string; endsAt: string | null }> {
  const res = await apiClient.post<{ data: { status: string; endsAt: string | null } }>(
    '/billing/cancel',
    {},
  )
  return res.data.data
}

/** Open a Stripe Customer Portal session; returns the URL to send the owner to. */
export async function openBillingPortal(): Promise<string> {
  const res = await apiClient.post<{ data: { url: string } }>('/billing/portal', {})
  return res.data.data.url
}

// ── Registro de actividad / audit log ──
export type AuditActorKind = 'user' | 'staff' | 'system'
export type AuditCategory = 'Configuración' | 'Conversación' | 'Equipo' | 'Acceso' | 'Facturación'
export interface AuditDetailRow {
  k: string
  v: string
  /** monospace value (event codes, folios). */
  mono?: boolean
}
export interface AuditEvent {
  id: string
  actorName: string
  actorInitials: string
  actorKind: AuditActorKind
  /** The human sentence, with the actor name bolded in the UI. */
  action: string
  /** "Dueño", "Asesor", "Equipo INHERTA", "Sistema". */
  context: string
  time: string
  /** ISO timestamp (for the date filter); `time` is the human display string. */
  occurredAt: string
  category: AuditCategory
  detail: AuditDetailRow[]
}

export interface ConnectedChannelView {
  channel: 'whatsapp' | 'instagram' | 'messenger'
  label: string
  account: string
}

export interface TenantSettings {
  profile: ProfileField[]
  /** Channels the agent answers on — read-only; the manager connects them. */
  channels: ConnectedChannelView[]
  fiscal: FiscalIdentityView
  exceptions: ScheduleException[]
  team: TeamMember[]
  /** Real tier usage (the tenant's actual allowance). Real plan/price/invoice
   *  history is a SEPARATE call, GET /billing (owner-only). */
  usage: UsageMeter[]
  manager: ManagerInfo
  activity: AuditEvent[]
}

/**
 * GET /settings — the workspace Configuración payload: profile, channels,
 * fiscal identity, exceptions, team, usage and the audit log. Manage-only
 * (owner/admin) — other roles that just need the assignable-member list use
 * GET /team/assignable instead.
 */
export async function getSettings(): Promise<TenantSettings> {
  const res = await apiClient.get<{ data: TenantSettings }>('/settings')
  return res.data.data
}

// ─── Team invites (G-L1b → POST /team/invitations) ───────────────────────────

/** The Select's UI role value → the tenant_memberships role the API expects.
 *  `visor` is no longer offered by any Select (tenant_viewer is hidden from the
 *  UI), but the mapping stays: an existing viewer's role value still round-trips
 *  through `uiRoleFor`, and dropping the entry would make it fall through to
 *  the `tenant_agent` default — a silent promotion. */
const INVITE_ROLE_API: Record<string, 'tenant_admin' | 'tenant_agent' | 'tenant_viewer'> = {
  agente: 'tenant_agent',
  administrador: 'tenant_admin',
  visor: 'tenant_viewer',
}

/** Invite a teammate. `uiRole` is the Select value (agente/administrador/visor). */
export async function inviteTeamMember(email: string, uiRole: string): Promise<void> {
  const role = INVITE_ROLE_API[uiRole] ?? 'tenant_agent'
  await apiClient.post('/team/invitations', { email, role })
}

/** Change an active member's role. `uiRole` is the Select value. */
export async function changeMemberRole(userId: string, uiRole: string): Promise<void> {
  const role = INVITE_ROLE_API[uiRole] ?? 'tenant_agent'
  await apiClient.patch(`/team/members/${encodeURIComponent(userId)}`, { role })
}

/** Remove an active member from the team. */
export async function removeMember(userId: string): Promise<void> {
  await apiClient.delete(`/team/members/${encodeURIComponent(userId)}`)
}

/** Transfer ownership to another member. The caller (current owner) steps down
 *  to administrator; both are signed out and must re-authenticate. */
export async function transferOwnership(userId: string): Promise<void> {
  await apiClient.post('/team/transfer-ownership', { userId })
}

/**
 * Send a teammate a password-reset email. The link only ever reaches their
 * mailbox: the caller never sees the token, so starting the recovery is not the
 * same as being able to finish it.
 */
export async function sendMemberPasswordReset(userId: string): Promise<void> {
  await apiClient.post(`/team/members/${encodeURIComponent(userId)}/password-reset`, {})
}

/** Clear a teammate's two-factor so they can enrol again. Closes every session
 *  they have open; `reason` records the identity check that was done first. */
export async function resetMemberMfa(userId: string, reason: string): Promise<void> {
  await apiClient.post(`/team/members/${encodeURIComponent(userId)}/mfa-reset`, { reason })
}

/** Resend a pending invitation (rotates the link). */
export async function resendInvitation(invitationId: string): Promise<void> {
  await apiClient.post(`/team/invitations/${encodeURIComponent(invitationId)}/resend`, {})
}

/** Cancel a pending invitation. */
export async function cancelInvitation(invitationId: string): Promise<void> {
  await apiClient.delete(`/team/invitations/${encodeURIComponent(invitationId)}`)
}

/** Map a canonical tenant role value to the Select's UI value (for editing). */
export function uiRoleFor(roleValue: string): string {
  if (roleValue === 'tenant_admin') return 'administrador'
  if (roleValue === 'tenant_viewer') return 'visor'
  return 'agente'
}

// ─── Settings content writes (G-L2) ──────────────────────────────────────────

export interface ProfileEdit {
  displayName?: string
  contactEmail?: string
  timezone?: string
}

/** Update the editable profile fields (display name + contact email + timezone). */
export async function saveProfile(edit: ProfileEdit): Promise<void> {
  await apiClient.put('/settings/profile', edit)
}

/** The four SAT fields a CFDI 4.0 needs from the receptor, plus the only
 *  derived fact that matters to the clinic: can we invoice them yet. */
export interface FiscalIdentityView {
  legalName: string
  rfc: string
  postalCode: string
  fiscalRegimen: string
  cfdiUse: string
  canInvoice: boolean
}

export type FiscalEdit = Partial<Omit<FiscalIdentityView, 'canInvoice'>>

/** One problem per wrong field, so the form can place each message rather than
 *  revealing them one save at a time. */
export interface FiscalProblemView {
  field: keyof FiscalEdit
  message: string
}

/** Save the clinic's own fiscal identity. Throws with `problems` attached when
 *  the API rejects it, so the caller can bind messages to fields. */
export async function saveFiscal(edit: FiscalEdit): Promise<{ canInvoice: boolean }> {
  const res = await apiClient.put<{ data: { ok: boolean; canInvoice: boolean } }>(
    '/settings/fiscal',
    edit,
  )
  return { canInvoice: res.data.data.canInvoice }
}

/** Pull the per-field problems out of a rejected save. Returns [] for anything
 *  that is not a fiscal validation failure, so callers can fall back to the
 *  generic message. */
export function fiscalProblemsOf(err: unknown): FiscalProblemView[] {
  const body = (err as { response?: { data?: { error?: { problems?: unknown } } } })?.response?.data
  const problems = body?.error?.problems
  return Array.isArray(problems) ? (problems as FiscalProblemView[]) : []
}

export interface ScheduleEdit {
  exceptions?: ScheduleException[]
  offHoursMessage?: string
}

// ─── Operación: the workspace's operating policies (client-ops §3) ───────────
// GET/PATCH /settings/operations, manage-only. The workspace assignment
// default RETIRED (Juan's 2026-08-19 lock): assignment is named policies
// attached per stage now (below). Only visibility remains here.

/** Who sees which CONVERSATIONS (F18: the workspace setting governs Bandeja
 *  only — each process carries its own 'Visibilidad de prospectos' in
 *  Procesos). 'all' = the whole team sees every conversation; 'assigned' = an
 *  Asesor sees their own plus whatever nobody has claimed, while Dueño and
 *  Administrador keep seeing all of it. */
export type VisibilityPolicy = 'all' | 'assigned'

/** Who a rule (and the balanced fallback) may hand a prospect to. */
export interface AssignableMemberView {
  id: string
  name: string
  role: string
}

export interface OperationsSettings {
  visibilityPolicy: VisibilityPolicy
}

export async function getOperations(): Promise<OperationsSettings> {
  const res = await apiClient.get<{ data: OperationsSettings }>('/settings/operations')
  return res.data.data
}

export async function saveOperations(edit: {
  visibilityPolicy?: VisibilityPolicy
}): Promise<OperationsSettings> {
  const res = await apiClient.patch<{ data: OperationsSettings }>('/settings/operations', edit)
  return res.data.data
}

// ─── Políticas de asignación: named entities, attached per stage ─────────────
// GET/POST/PUT/DELETE /settings/assignment-policies. A policy is a name, an
// ORDERED rule list (first match wins) and an EXPLICIT fallback for when no
// rule matches — 'none' by default, never a silent balanced.

/** What happens when no rule of a policy matches. */
export type PolicyFallback = 'balanced' | 'none'

/** One routing rule: "cuando <campo> es <valor>, el prospecto es de <asesor>".
 *  Ordered — the first match wins. */
export interface AssignmentRuleView {
  field: string
  equals: string
  memberId: string
}

export interface AssignmentPolicyView {
  id: string
  name: string
  rules: AssignmentRuleView[]
  fallback: PolicyFallback
  /** Stages currently attached to this policy — the delete guard's number. */
  inUseStageCount: number
}

export interface AssignmentPoliciesSettings {
  policies: AssignmentPolicyView[]
  members: AssignableMemberView[]
}

export interface AssignmentPolicyEdit {
  name: string
  /** Sent WHOLE, in order — the editor rewrites the list, it never patches a
   *  row, so the server never has to merge two people's edits. */
  rules: AssignmentRuleView[]
  fallback: PolicyFallback
}

export async function getAssignmentPolicies(): Promise<AssignmentPoliciesSettings> {
  const res = await apiClient.get<{ data: AssignmentPoliciesSettings }>(
    '/settings/assignment-policies',
  )
  return res.data.data
}

export async function createAssignmentPolicy(
  edit: AssignmentPolicyEdit,
): Promise<AssignmentPoliciesSettings> {
  const res = await apiClient.post<{ data: AssignmentPoliciesSettings }>(
    '/settings/assignment-policies',
    edit,
  )
  return res.data.data
}

export async function updateAssignmentPolicy(
  id: string,
  edit: AssignmentPolicyEdit,
): Promise<AssignmentPoliciesSettings> {
  const res = await apiClient.put<{ data: AssignmentPoliciesSettings }>(
    `/settings/assignment-policies/${encodeURIComponent(id)}`,
    edit,
  )
  return res.data.data
}

export async function deleteAssignmentPolicy(id: string): Promise<AssignmentPoliciesSettings> {
  const res = await apiClient.delete<{ data: AssignmentPoliciesSettings }>(
    `/settings/assignment-policies/${encodeURIComponent(id)}`,
  )
  return res.data.data
}
